import React, { createContext, useContext, useState } from "react";
import { authApi } from "../api/client";
import { clearActiveTaskId, clearActiveTrackId, clearSession, getStoredSession, saveSession } from "../utils/session";
import { resetInvalidTokenHandler } from "../api/http";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const initial = getStoredSession();
  const [token, setToken] = useState(initial.token);
  const [user, setUser] = useState(initial.user);

  const clearAuthState = () => {
    setToken(null);
    setUser(null);
    clearSession();
    clearActiveTrackId();
    clearActiveTaskId();
    resetInvalidTokenHandler();
  };

  const setAuthSession = (nextToken, nextUser) => {
    setToken(nextToken);
    setUser(nextUser);
    saveSession(nextToken, nextUser);
    resetInvalidTokenHandler();
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore network/API logout failures and clear locally.
    }
    clearAuthState();
  };

  const refreshMe = async () => {
    const { data } = await authApi.me();
    if (data?.user) {
      setUser(data.user);
      saveSession(token, data.user);
    }
    return data?.user;
  };

  const value = {
    token,
    user,
    isAuthenticated: Boolean(token),
    setAuthSession,
    setUser,
    logout,
    clearAuthState,
    refreshMe,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
